// src/reducers/breadcrumbReducer.js
//
// Context Stack pattern for custom-app breadcrumbs.
// Each custom-app workspace gets an ordered array of { type, id } items
// representing exactly what is shown left-to-right in the breadcrumb.
//
// Non-custom-app workspaces continue to use the legacy SET/CLEAR_BREADCRUMB_SELECTION
// path so that unrelated breadcrumb behaviour is not disturbed.

// ── Action types ─────────────────────────────────────────────────────────────
const PUSH_BREADCRUMB_ITEM = "PUSH_BREADCRUMB_ITEM";
const INSERT_BREADCRUMB_ITEM_AFTER = "INSERT_BREADCRUMB_ITEM_AFTER";
const REMOVE_BREADCRUMB_ITEM = "REMOVE_BREADCRUMB_ITEM";
const POP_BREADCRUMB_TO_ID = "POP_BREADCRUMB_TO_ID";
const CLEAR_BREADCRUMB_STACK = "CLEAR_BREADCRUMB_STACK";
// Legacy (non-custom-app workspaces)
const SET_BREADCRUMB_SELECTION = "SET_BREADCRUMB_SELECTION";

// ── Action creators ───────────────────────────────────────────────────────────

/**
 * Push { type: 'report'|'group'|'folder', id: string } onto the workspace stack.
 * If the item is already present the reducer pops to it instead (back-navigation).
 */
export const pushBreadcrumbItem = (workspaceId, item) => ({
  type: PUSH_BREADCRUMB_ITEM,
  payload: { workspaceId, item },
});

/**
 * Insert item immediately after the stack entry whose id === afterId.
 * If afterId is not found, appends to the end. No-op if item already present.
 */
export const insertBreadcrumbItemAfter = (workspaceId, item, afterId) => ({
  type: INSERT_BREADCRUMB_ITEM_AFTER,
  payload: { workspaceId, item, afterId },
});

/** Remove a single item from the stack by id without affecting any other entries. */
export const removeBreadcrumbItem = (workspaceId, itemId) => ({
  type: REMOVE_BREADCRUMB_ITEM,
  payload: { workspaceId, itemId },
});

/** Truncate the stack so that itemId is the last (active) entry. */
export const popBreadcrumbToId = (workspaceId, itemId) => ({
  type: POP_BREADCRUMB_TO_ID,
  payload: { workspaceId, itemId },
});

/** Remove the entire stack for a workspace (e.g. when closing the app tab). */
export const clearBreadcrumbStack = (workspaceId) => ({
  type: CLEAR_BREADCRUMB_STACK,
  payload: { workspaceId },
});

// Legacy action creators kept for non-custom-app code paths
export const setBreadcrumbSelection = (workspaceId, selection) => ({
  type: SET_BREADCRUMB_SELECTION,
  payload: { workspaceId, selection },
});
export const clearBreadcrumbSelection = clearBreadcrumbStack;

// ── Reducer ───────────────────────────────────────────────────────────────────

const INITIAL_STATE = {
  // Custom-app stacks: { [workspaceId]: Array<{ type, id }> }
  stackByWorkspace: {},
  // Legacy flat selections for non-custom-app workspaces
  lastSelectionByWorkspace: {},
};

export default function breadcrumbReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case PUSH_BREADCRUMB_ITEM: {
      const { workspaceId, item } = action.payload;
      if (!workspaceId || !item?.id) return state;
      const current = state.stackByWorkspace[workspaceId] || [];

      // Already in the stack → no-op. Items are only removed via (x) / popBreadcrumbToId / clearBreadcrumbStack.
      if (current.some((i) => i.id === item.id)) {
        return state;
      }
      const next = [...current, item];

      return {
        ...state,
        stackByWorkspace: { ...state.stackByWorkspace, [workspaceId]: next },
      };
    }
    case INSERT_BREADCRUMB_ITEM_AFTER: {
      const { workspaceId, item, afterId } = action.payload;
      if (!workspaceId || !item?.id) return state;
      const current = state.stackByWorkspace[workspaceId] || [];
      if (current.some((i) => i.id === item.id)) return state; // no-op if already present
      const afterIdx = current.findIndex((i) => i.id === afterId);
      const insertAt = afterIdx === -1 ? current.length : afterIdx + 1;
      const next = [...current.slice(0, insertAt), item, ...current.slice(insertAt)];

      return {
        ...state,
        stackByWorkspace: { ...state.stackByWorkspace, [workspaceId]: next },
      };
    }
    case REMOVE_BREADCRUMB_ITEM: {
      const { workspaceId, itemId } = action.payload;
      if (!workspaceId || !itemId) return state;
      const current = state.stackByWorkspace[workspaceId] || [];
      const next = current.filter((i) => i.id !== itemId);
      if (next.length === current.length) return state; // nothing removed

      return {
        ...state,
        stackByWorkspace: { ...state.stackByWorkspace, [workspaceId]: next },
      };
    }
    case POP_BREADCRUMB_TO_ID: {
      const { workspaceId, itemId } = action.payload;
      if (!workspaceId || !itemId) return state;
      const current = state.stackByWorkspace[workspaceId] || [];
      const idx = current.findIndex((i) => i.id === itemId);

      if (idx === -1) return state;
      return {
        ...state,
        stackByWorkspace: {
          ...state.stackByWorkspace,
          [workspaceId]: current.slice(0, idx + 1),
        },
      };
    }
    case CLEAR_BREADCRUMB_STACK: {
      const { workspaceId } = action.payload;

      if (!workspaceId) return state;
      const nextStacks = { ...state.stackByWorkspace };
      delete nextStacks[workspaceId];
      // Also clear legacy entry if present
      const nextSelections = { ...state.lastSelectionByWorkspace };
      delete nextSelections[workspaceId];
      return { ...state, stackByWorkspace: nextStacks, lastSelectionByWorkspace: nextSelections };
    }
    case SET_BREADCRUMB_SELECTION: {
      const { workspaceId, selection } = action.payload || {};
      if (!workspaceId || !selection) return state;
      return {
        ...state,
        lastSelectionByWorkspace: {
          ...state.lastSelectionByWorkspace,
          [workspaceId]: selection,
        },
      };
    }
    default:
      return state;
  }
}
